import { useCallback, useEffect, useMemo, useState } from 'react';

import { Button } from '@bambi/component-library/src/components/Button/Button';
import { ContextFormFieldText } from '@bambi/component-library/src/components/forms/context/ContextFormFieldText';
import { LoadingIndicator } from '@bambi/component-library/src/components/LoadingIndicator/LoadingIndicator';
import { datadogLogs } from '@datadog/browser-logs';
import { DocumentCheckIcon } from '@heroicons/react/24/outline';
import { usePDF } from '@react-pdf/renderer';
import { groupBy } from 'lodash-es';
import { FormProvider, SubmitHandler, useForm } from 'react-hook-form';

import { useAppDispatch, useAppSelector } from 'src/app/store/hooks';
import {
  useBillingBillingSettingsRetrieveQuery,
  useOrganizationSettingsLogoRetrieveQuery,
  useOrganizationSettingsRetrieveQuery,
} from 'src/common/external/bambi-api/bambiApi';
import { FormErrorMessage } from 'src/common/FormErrorMessage';
import { FormField } from 'src/common/FormField';
import { FormFieldController } from 'src/common/FormField/FormFieldController';
import { selectCurrentOrganization } from 'src/features/auth/selectors';
import { InvoicePDFDocument } from 'src/features/billing/invoices/pdf/invoice/InvoicePDFDocument';
import { ReceiptPDFDocument } from 'src/features/billing/invoices/pdf/receipt/ReceiptPDFDocument';
import { InvoiceExportCondensedReportItem } from 'src/features/billing/invoices/types';

import {
  setInvoiceExportBlobUrl,
  setInvoiceExportMode,
} from '../../../invoice.slice';
import { useInvoiceBlobContext } from '../../InvoiceBlobContext';
import {
  columnOptions,
  columnOptionsMap,
  InvoiceColumnSelect,
  InvoiceSelectedOptions,
} from './components/InvoiceColumnSelect';

type PDFGenerationOptions = {
  fileName: string;
  invoiceNote: string;
  includePriceSummary: boolean;
  selectedInvoiceColumns: InvoiceSelectedOptions;
  generateAsReceipt: boolean;
  includePastDue: boolean;
};

interface CondensedPDFStep2Props {
  onClose?: () => void;
  next: () => void;
  prev: () => void;
}

export function CondensedPDFStep2({
  onClose,
  next,
  prev,
}: CondensedPDFStep2Props) {
  const currentOrganization = useAppSelector(selectCurrentOrganization);
  const dispatch = useAppDispatch();

  const report = useAppSelector((state) => state.invoice.exportedCondensedData);
  const payments = useAppSelector(
    (state) => state.invoice.exportedInvoicePaymentsData
  );
  const refunds = useAppSelector(
    (state) => state.invoice.exportedInvoiceRefundsData
  );
  const exportMode = useAppSelector((state) => state.invoice.invoiceExportMode);
  const { data: billingSettings, isLoading: isLoadingBillingSettings } =
    useBillingBillingSettingsRetrieveQuery({});
  const { data: orgSettings, isLoading: isLoadingOrgSettings } =
    useOrganizationSettingsRetrieveQuery({});
  const [showPdfLink, setShowPdfLink] = useState(false);
  const { setBlobUrlBlobMap } = useInvoiceBlobContext();

  const invoices = groupBy(report?.records ?? [], (r) => r.InvoiceID);
  const payers = groupBy(report?.records ?? [], (r) => r.ItemPayerID);
  const tripLineItems =
    report?.records.filter((r) => r.LineItemType === 'trip') ?? [];
  const trips = groupBy(tripLineItems, (r) => r.TripID);

  const form = useForm<PDFGenerationOptions>({
    mode: 'onChange',
    defaultValues: {
      fileName: report?.fileName,
      includePriceSummary: true,
      selectedInvoiceColumns: columnOptions,
      generateAsReceipt: false,
      includePastDue: true,
    },
  });

  useEffect(() => {
    if (isLoadingBillingSettings) {
      return;
    }

    form.setValue('invoiceNote', billingSettings?.invoice_note ?? '');
    const columnSettings =
      billingSettings?.invoice_generation_columns?.split(',') || [];
    const invoiceGenerationOptions = columnOptions.filter((option) =>
      columnSettings.includes(option.value)
    );
    form.setValue('selectedInvoiceColumns', invoiceGenerationOptions);
  }, [billingSettings, form, isLoadingBillingSettings]);

  // Group invoice data by invoice id
  const invoiceItems = useMemo<InvoiceExportCondensedReportItem[]>(() => {
    const items: InvoiceExportCondensedReportItem[] = [];

    Object.keys(invoices).forEach((invoiceId) => {
      const recordsByInvoice = invoices[invoiceId];
      const firstItem = recordsByInvoice[0];

      const item: InvoiceExportCondensedReportItem = {
        InvoiceID: invoiceId,
        InvoiceNo: firstItem.InvoiceNo,
        ItemPayerID: firstItem.ItemPayerID,
        ItemPayerName: firstItem.ItemPayerName,
        items: recordsByInvoice,
        InvoiceDueAmount: firstItem.InvoiceDueAmount,
        PaymentsTotalAmount: firstItem.PaymentsTotalAmount,
        ItemPayerBillingAddress: firstItem.ItemPayerBillingAddress,
        ItemPayerBillingContactName: firstItem.ItemPayerBillingContactName,
        ItemPayerBillingContactPhone: firstItem.ItemPayerBillingContactPhone,
        ItemPayerBillingContactEmail: firstItem.ItemPayerBillingContactEmail,
        ItemPayerInvoiceDueDays: firstItem.ItemPayerInvoiceDueDays,
        InvoiceNotes: firstItem.InvoiceNotes,
        ModifiersTotalAmount: firstItem.ModifiersTotalAmount,
        TripsTotalAmount: firstItem.TripsTotalAmount,
        InvoiceDate: firstItem.InvoiceDate,
        RefundsTotalAmount: firstItem.RefundsTotalAmount,
        ItemPayerAlternateName: firstItem.ItemPayerAlternateName,
      };

      items.push(item);
    });

    return items;
  }, [invoices]);

  const [instance, update] = usePDF();

  const { data: orgLogo } = useOrganizationSettingsLogoRetrieveQuery({});

  const orgLogoAsBase64 = orgLogo?.logo;
  const reportFileName = form.watch('fileName');

  const handleRenderPdf = useCallback<SubmitHandler<PDFGenerationOptions>>(
    ({
      invoiceNote,
      selectedInvoiceColumns,
      includePriceSummary,
      generateAsReceipt,
      includePastDue,
    }) => {
      const resolvedSelectedInvoiceColumns = selectedInvoiceColumns.map(
        (option) => {
          if (typeof option !== 'string') {
            return option;
          }
          const resolvedOption = columnOptionsMap[option];
          return resolvedOption;
        }
      );

      update(
        generateAsReceipt && payments?.length ? (
          <ReceiptPDFDocument
            organizationName={currentOrganization?.name || ''}
            organizationBillingAddress={
              orgSettings?.billing_address ??
              orgSettings?.headquarters_location?.address ??
              ''
            }
            organizationBillingPhone={billingSettings?.phone_number ?? ''}
            organizationBillingEmail={billingSettings?.email ?? ''}
            organizationLogo={orgLogoAsBase64 ?? ''}
            organizationTaxId={orgSettings?.tax_id ?? ''}
            invoiceItems={invoiceItems}
            invoiceNote={invoiceNote}
            includePriceSummary={includePriceSummary}
            selectedInvoiceColumns={resolvedSelectedInvoiceColumns}
            invoicePayments={payments}
            invoiceRefunds={refunds ?? []}
          />
        ) : (
          <InvoicePDFDocument
            organizationName={currentOrganization?.name || ''}
            organizationBillingAddress={
              orgSettings?.billing_address ??
              orgSettings?.headquarters_location?.address ??
              ''
            }
            organizationBillingPhone={billingSettings?.phone_number ?? ''}
            organizationBillingEmail={billingSettings?.email ?? ''}
            organizationLogo={orgLogoAsBase64 ?? ''}
            organizationTaxId={orgSettings?.tax_id ?? ''}
            invoiceItems={invoiceItems}
            invoiceNote={invoiceNote}
            includePriceSummary={includePriceSummary}
            selectedInvoiceColumns={resolvedSelectedInvoiceColumns}
            includePastDueLabel={includePastDue}
          />
        )
      );

      if (exportMode === 'DOWNLOAD') {
        setShowPdfLink(true);
      }
    },
    [
      billingSettings?.email,
      billingSettings?.phone_number,
      currentOrganization?.name,
      exportMode,
      invoiceItems,
      orgLogoAsBase64,
      orgSettings?.billing_address,
      orgSettings?.headquarters_location?.address,
      orgSettings?.tax_id,
      payments,
      refunds,
      update,
    ]
  );

  // When exporting as a Blob, store in context then close
  useEffect(() => {
    if (
      exportMode === 'BLOB' &&
      !instance.loading &&
      instance.blob &&
      instance.url
    ) {
      setBlobUrlBlobMap((current) => {
        return {
          ...current,
          [instance.url as string]: instance.blob as Blob,
        };
      });
      dispatch(setInvoiceExportBlobUrl(instance.url));
      // Reset to download
      dispatch(setInvoiceExportMode('DOWNLOAD'));
      onClose?.();
    }
  }, [
    dispatch,
    exportMode,
    instance.blob,
    instance.loading,
    instance.url,
    onClose,
    setBlobUrlBlobMap,
  ]);

  // Somehow skipped step 1, or was unable to assign report data
  if (!report?.records.length) {
    return (
      <div className="mt-2">
        <h2>Something went wrong, unable to find any records.</h2>
        <span>Go back to the previous step and adjust your filters.</span>
      </div>
    );
  }

  // Loading initial settings
  if (isLoadingBillingSettings || isLoadingOrgSettings) {
    return (
      <div className="mt-2 flex flex-row justify-center">
        <LoadingIndicator />
      </div>
    );
  }

  // Last step, we have data and generation settings go ahead and render the
  // document link
  if (showPdfLink) {
    if (instance.loading || !instance.url) {
      return (
        <div className="mt-2 flex flex-row justify-center">
          <div className="flex flex-col items-center gap-2">
            <img
              className="aspect-square h-12 w-12"
              src={process.env.PUBLIC_URL + '/icons/document.gif'}
              alt="Rendering PDF"
              data-attribution="Paper animated icons created by Freepik - Flaticon"
            />
            Rendering PDF
          </div>
        </div>
      );
    }

    // Error rendering PDF
    if (instance.error) {
      return (
        <div className="mt-2 flex flex-row justify-center">
          <FormErrorMessage>
            Something unexpected happened while rendering: {instance.error}
          </FormErrorMessage>
        </div>
      );
    }

    // PDF ready
    return (
      <div className="mt-2 flex flex-row justify-center">
        <div className="mt-2 flex flex-row">
          <span className="text-green-500">
            <DocumentCheckIcon width={32} height={32} />
          </span>
          <a href={instance.url} download={`${reportFileName}.pdf`}>
            <span
              className="text-md border-none px-3 py-2 text-blue-600 underline"
              onClick={() => {
                // Delaying slightly because we need the link to open before
                // closing the modal so we don't kill the reference to the blob
                // that was just created
                setTimeout(() => onClose?.(), 500);
                datadogLogs.logger.warn('invoice-export:v1');
              }}
            >
              Click here to download the report
            </span>
          </a>
        </div>
      </div>
    );
  }

  // Step 1 - Generation options
  return (
    <div className="mt-2">
      <FormProvider {...form}>
        <div>
          <div className="flex flex-col gap-2">
            <div className="my-4 flex flex-row justify-evenly gap-2">
              <div className="text-2xl" data-testid="invoice-count-stat-block">
                <strong>{Object.keys(invoices).length}</strong> Invoices
              </div>
              <div className="text-2xl" data-testid="payer-count-stat-block">
                <strong>{Object.keys(payers).length}</strong> Payers
              </div>
              <div className="text-2xl" data-testid="trip-count-stat-block">
                <strong>{Object.keys(trips).length}</strong> Trips
              </div>
            </div>
            <p>
              Customize invoice settings before generating the PDF. Any options
              selected here will override the default payer settings for each
              invoice.
            </p>
            <strong>Generation options:</strong>
            <div className="flex flex-row gap-2">
              <FormFieldController name="includePriceSummary">
                {({ field, fieldState }) => {
                  return (
                    <FormField
                      label="Include Price Summary"
                      error={fieldState.error?.message?.toString()}
                      type="switch"
                      inputProps={{
                        id: 'includePriceSummary',
                        ...field,
                        checked: field.value,
                      }}
                    />
                  );
                }}
              </FormFieldController>
              <div style={{ maxWidth: '10rem' }}>
                <FormFieldController name="generateAsReceipt">
                  {({ field, fieldState }) => {
                    return (
                      <FormField
                        label="Generate as Receipt"
                        helperText="If the invoice has payments, include payment details and other receipt treatments."
                        error={fieldState.error?.message?.toString()}
                        type="switch"
                        inputProps={{
                          id: 'generateAsReceipt',
                          ...field,
                          checked: field.value,
                          disabled: !payments?.length,
                        }}
                      />
                    );
                  }}
                </FormFieldController>
              </div>
              <div style={{ maxWidth: '10rem' }}>
                <FormFieldController name="includePastDue">
                  {({ field }) => {
                    return (
                      <FormField
                        label="Include Past Due Label"
                        helperText="If the invoice is past its due date, include the red PAST DUE label."
                        type="switch"
                        inputProps={{
                          id: 'includePastDue',
                          ...field,
                          checked: field.value,
                        }}
                      />
                    );
                  }}
                </FormFieldController>
              </div>
            </div>

            <InvoiceColumnSelect
              fieldPath="selectedInvoiceColumns"
              label="Invoice columns"
            />
            <ContextFormFieldText fieldPath="fileName" label="Filename" />
            <ContextFormFieldText
              fieldPath="invoiceNote"
              label="Note to payer"
            />
          </div>
          <div className="grid grid-cols-2 gap-4 pt-6">
            <Button onClick={onClose}>Cancel</Button>
            <Button
              dataTestId="create-pdf"
              variant="primary"
              onClick={form.handleSubmit(handleRenderPdf)}
            >
              Create PDF
            </Button>
          </div>
        </div>
      </FormProvider>
    </div>
  );
}
